/**
 * boardSlots.ts — turning a contract into the rows of the signing board.
 *
 * A SLOT IS A DOCUMENT THAT COULD EXIST, not one that does. The board has to show the
 * hire purchase agreement before anybody has generated it, and it has to show one deposit
 * agreement per deposit — so the rows come from the contract's SUBJECTS (the contract
 * itself, the loans on the insurance junction it was set up on, its deposits) and a document
 * is then matched onto the slot it was generated for.
 *
 * THE MATCH IS BY ARC ID, NEVER BY DOCUMENT TYPE. `get_agreement_documents` carries no row
 * identifiers, which is why `agreement_documents` is read separately for its five exclusive
 * arc columns: a contract with three deposits has three DEPOSIT documents and only the arc
 * says which is which. Matching on (contract, doc_type) would collapse them into one row
 * and attach the wrong signatures to the wrong money.
 *
 * THE FIFTH ARC IS THE VEHICLE SCHEDULE'S [D-B48]. Its subject is the CONTRACT, which it shares
 * with the hire purchase and witness agreements, so three of this board's rows are minted from one
 * subject id and told apart by `slotKey`'s doc-type half.
 */

import { format } from 'date-fns';
import { businessDay, formatCurrency } from '@/lib/format';
import { DOC_TYPES, isLiveDocument, isThirdPartyVehicle } from '@/lib/agreements/constants';
import type { AgreementDocType } from '@/lib/agreements/constants';
import {
    FULL_DEPOSIT_TOLERANCE,
    INSTRUMENT_TITLES,
    INSURANCE_AT_END_TEMPLATE_KEY,
    TEMPLATE_KEY_BY_INSTRUMENT,
    instrumentFor,
    type InsuranceInstrument,
} from '@/lib/agreements/instrument';
import type { AgreementDocumentArcs, AgreementDocumentRow } from '@/lib/agreements/api';

import { partnerScheduleCashPriceRefusal } from './partnerSchedule';

export interface LegacyAgreementRow {
    id: number;
    signed_at: string | null;
    agreement_url: string | null;
    origin: string | null;
}

/**
 * One `contract_partner_agreements` row — the Vehicle Schedule's referent [D-B48].
 *
 * `LegacyAgreementRow` PLUS THE THREE FACTS A BOARD READS OFF IT. `partner_id` is which owner the
 * row is about — derived by trigger from the contract and the vehicle, frozen at the partner's
 * signature, never client-written — so it is the only partner id this app may compare against.
 * `vehicle_id` is the car it was raised about, and `management_rate` is the fee the schedule was
 * raised on; the driver-signature gate tests both against the contract's live ones.
 *
 * THE CASH PRICE COPIED ONTO IT IS NOT READ HERE [D-B59]. `agreement_create` copies the contract's
 * `cash_price` onto `all_in_cost` at generation, so the board states the contract's own figure
 * (`BoardSlotInput.cashPrice`) and the copy is the partner roster's to show
 * (`partners/partnerAgreements.ts`), which is the only surface that reads a sealed record's figure.
 *
 * DECLARED HERE RATHER THAN BESIDE ITS READ, exactly as `LegacyAgreementRow` is: `boardSubjects`
 * fetches the rows and imports the shape, so the slot layer owns what a subject looks like and the
 * two modules keep the one-way dependency they have always had.
 */
export interface PartnerAgreementRow extends LegacyAgreementRow {
    partner_id: number | null;
    /**
     * WHICH CAR the schedule was raised about — derived by trigger from the contract, frozen at the
     * partner's signature, `NOT NULL` in the schema (`20260908010000:286`).
     *
     * READ BECAUSE THE DRIVER-SIGNATURE GATE READS IT, and it is not made redundant by
     * `partner_id`: one partner can own two cars, and a contract whose vehicle moves between them
     * past DRAFT keeps the referent it already had (`contracts_void_partner_schedule_on_money_change`
     * is silent outside DRAFT). The gate's `cpa.vehicle_id = contracts.vehicle_id` limb is what
     * catches that, so a board without this column cannot answer the question the server asks.
     */
    vehicle_id: number | null;
    management_rate: number | null;
}

export interface SubjectWithAgreement {
    id: number;
    amount?: number | null;
    dated?: string | null;
    /**
     * THE TWO LIMBS OF THE INSURANCE INSTRUMENT [D-B45], READ ONLY ON AN `insurance_loans` SUBJECT.
     *
     * They are optional because the deposit subjects share this shape and neither limb means
     * anything about a contract deposit — and because absent is the answer that falls to the
     * advance, which is what a subject read that has not been widened yet should produce. See
     * `instrumentFor`.
     */
    depositTotal?: number | null;
    instalmentCount?: number | null;
    agreement: LegacyAgreementRow | null;
}

/**
 * The identity of a slot: one document type on one subject.
 *
 * BOTH HALVES ARE LOAD-BEARING. HIRE_PURCHASE and WITNESS are generated from the SAME
 * subject — the contract itself — so a key of subject alone makes those two rows
 * indistinguishable, and anything keyed that way (a React key, a pending spinner) lands on
 * both at once.
 */
export function slotKey(docType: AgreementDocType, subjectId: number): string {
    return `${docType}:${subjectId}`;
}

export interface BoardSlot {
    /** `slotKey(docType, subjectId)` — the React key, and how a pending action finds its row. */
    key: string;
    docType: AgreementDocType;
    /** What `agreement_create` is called with. */
    subjectId: number;
    /**
     * WHAT THIS ROW IS CALLED, AND THE DOCUMENT'S OWN TITLE OUTRANKS EVERYTHING [D-B45].
     *
     * `DOC_TYPES[docType].label` used to be the whole answer, and it stopped being one when a
     * second instrument joined `INSURANCE_LOAN`: one doc type now carries two published titles,
     * so a name derived from the type alone would call an Insurance Premium Payment Record an
     * advance on every surface that draws this row.
     *
     * A GENERATED DOCUMENT ANSWERS FOR ITSELF. `agreement_templates.title` is fetched with every
     * document (`api.ts`), it is the string printed as the instrument's own H1, and it is pinned
     * on the row for the life of the document — so it cannot drift from the page it names. An
     * EMPTY slot has no document to ask, and is named by the instrument it WOULD be generated
     * under (`instrument` below).
     */
    title: string;
    /**
     * WHICH INSURANCE INSTRUMENT THIS SLOT PROPOSES, on `INSURANCE_LOAN` rows and nowhere else
     * (null on every other type) [D-B45].
     *
     * IT IS THE SUBJECT'S SHAPE, NOT THE DOCUMENT'S, and that is the useful reading rather than
     * the lazy one. `agreement_create` derives the instrument from the loan's rows AT THE MOMENT
     * OF GENERATION, so what an empty slot must promise is what the loan looks like NOW; and
     * `agreement_sign` re-asserts the same shape, so a document that no longer matches its subject
     * is already refused with `AGREEMENT_INSTRUMENT_MISMATCH` — a sentence that names the real
     * problem and whose fix is void-and-reissue. A slot that read the instrument off its stale
     * document instead would hide the countersign button behind a witness sentence that is not
     * what is wrong.
     */
    instrument: InsuranceInstrument | null;
    /**
     * WHICH PAGE `agreement_create` WOULD PICK FOR THIS SUBJECT RIGHT NOW — on `INSURANCE_LOAN`
     * rows and nowhere else (null on every other type) [D-B57].
     *
     * IT IS THE SERVER'S THREE-WAY SELECTOR, NOT THE TWO-VALUED INSTRUMENT BESIDE IT. One
     * `doc_type` publishes THREE pages: the Record [D-B45], the ordinary Advance, and the Advance
     * settled at the end of the contract — and the last two carry the SAME title and the same
     * instrument, so `instrument` above cannot tell them apart and is not meant to. This is what a
     * surface asks when it needs the page rather than the name.
     *
     * IT ANSWERS FOR THE SUBJECT, AND THE DOCUMENT ON THE ROW IS NOT CONSULTED — the same reading
     * `instrument` takes, for the same reason: `agreement_create` derives the page from the loan's
     * rows at the MOMENT of generation, so what this has to carry is what the loan looks like NOW.
     * The row compares it against the page its document actually pinned, and that comparison is the
     * whole point of holding the two separately.
     *
     * NULL IS "I DO NOT KNOW", NOT "NO GAP". A subject read that never fetched the instalment count
     * cannot answer the server's question, and a Record the registry cannot raise must not be
     * named — see the derivation. Every consumer treats null as silence: `agreement_sign` asks the
     * same question against the database and refuses out loud at the next signature, which is a
     * better answer than a guess.
     */
    expectedTemplateKey: string | null;
    /** A qualifier shown beside the title when a contract holds several of a type. */
    label?: string;
    subtitle?: string;
    legacyRowId: number | null;
    legacySignedAt: string | null;
    legacyUrl: string | null;
    legacyOrigin: string | null;
    /**
     * THE CONTRACT'S CASH PRICE — on `PARTNER_MANAGEMENT` rows and nowhere else (null on every
     * other type) [D-B59].
     *
     * IT IS `BoardSlotInput.cashPrice`, NEVER THE REFERENT'S COPY. `agreement_create` copies
     * `contracts.cash_price` onto the record at create and on every re-issue over an unsealed slot
     * with no live document, so the figure a generate or a reissue WILL state is the contract's
     * current one. The generate dialog shows it, and holds Generate shut where it is null or
     * nothing; a figure read off the referent would show what a previous schedule stated instead.
     */
    cashPrice: number | null;
    /** The live document on this slot — VOIDED and SUPERSEDED ones are never live. */
    document: AgreementDocumentRow | null;
    /**
     * Set when this slot cannot be acted on yet, and why.
     *
     * THE TWO CONTRACT ARCS SET IT ONLY BEFORE THEIR DOCUMENT EXISTS — a generated instrument has
     * frozen its parties and its figures, so a precondition of MINTING has nothing left to say. The
     * premium advance's lock is not one of those: it holds the whole first move (`advanceLockedFor`)
     * and therefore stands whether or not the document has been minted already.
     */
    blockedReason: string | null;
    /** The setup step that clears `blockedReason`. */
    blockedStep: string | null;
    /**
     * THE TWO CROSS-DOCUMENT FACTS [D-B18], DERIVED ONCE AND HUNG ON EVERY SLOT.
     *
     * They are facts about the CONTRACT, not about the row that carries them — which is exactly why
     * they have to travel on the slot. `agreement_sign` refuses a Primelane countersignature on a
     * hire purchase agreement, a witness agreement OR an insurance premium advance [D-B27] until
     * the driver has signed the hire purchase agreement AND the witness has signed the witness
     * agreement (`AGREEMENT_COUNTERSIGN_BLOCKED`), and a row only ever sees its own document — so
     * without these a board would offer a countersign button the server answers with a refusal.
     * (The insurance premium PAYMENT RECORD asks the driver's arm alone [D-B45]; the server's own
     * carve-out, mirrored in `siblingSignatureOutstanding`.)
     *
     * EACH HAS THE SERVER'S TWO SOURCES, IN THE SERVER'S ORDER: a SIGNED seat on the live document
     * of that type, OR a legacy `contract_agreements` row of that type carrying a `signed_at`. The
     * legacy half is not a fallback for old data — 174 production contracts signed their hire
     * purchase agreement on paper and will never have an in-app DRIVER seat at all.
     */
    driverSigned: boolean;
    /** The witness has signed the WITNESS agreement — its WITNESS seat, or the legacy WITNESS row. */
    witnessSigned: boolean;
    /**
     * THIS CONTRACT'S HIRE PURCHASE AGREEMENT CAME BACK ON PAPER, AND THAT IS THE WHOLE RECORD
     * [D-B53] — the fourth contract fact, derived once beside the three others and for their reason.
     *
     * BOTH HALVES, BECAUSE EITHER ALONE NAMES A DIFFERENT CONTRACT, and they are the server's own
     * two: a `contract_agreements` DRIVER row carrying a `signed_at` AND a paper `origin`, and NO
     * live hire purchase document of any status. The paper row alone would answer true on a
     * contract whose in-app agreement is mid-ceremony, where the witness's attestation is exactly
     * what is being waited for; the absent document alone would answer true on a contract nobody
     * has signed anything on at all, where the driver's arm is the one that refuses.
     *
     * THE `origin` DISCRIMINATOR IS NOT DECORATION [D-B52]. `agreement_finalize` writes `signed_at`
     * back onto the same legacy row when an in-app ceremony completes, so a date alone would read
     * an in-app execution as a paper one; NULL reads as SCANNED, because every row that carried a
     * date before the column existed came back on paper.
     *
     * IT IS READ BY ONE RULE ON EACH OF THE TWO SURFACES THE SETUP PAGE MOUNTS, and by nothing
     * else. `siblingSignatureOutstanding` drops the WITNESS arm on the two advances where this is
     * true — see it for why — and `setupState.countersignBlockedNote` drops the same arm from the
     * manifest's narration off the copy `snapshotAdapter.agreementFacts` carries across, because a
     * note that still said "the witness has to sign" would be contradicting the countersign button
     * enabled beside it on the same page.
     */
    masterOnPaper: boolean;
    /**
     * THE THIRD CROSS-DOCUMENT FACT [D-B48], derived once and hung on every slot for the reason
     * the two above are: it is a fact about the CONTRACT, and the row it governs is not the row it
     * is about.
     *
     * `partnerScheduleRequired` is whether this contract's vehicle belongs to a third-party
     * partner at all — 30 of the 289 vehicles — read off `vehicles.partner_id` against the house
     * marker. On Primelane's own fleet there is no schedule, no slot and nothing to wait for.
     *
     * `partnerScheduleSigned` is the SERVER'S WHOLE `NOT EXISTS`, not just its seal half: a referent
     * about THIS contract's current vehicle, raised at the fee the contract currently charges (or
     * at no stated fee at all, which the SQL admits outright), carrying either its own `signed_at`
     * or a SIGNED PARTNER seat on the document arced to IT. The seal limb is not a fallback for old
     * data — it is what an in-app schedule looks like the moment `agreement_finalize` writes the
     * seal back, and it is the only limb the twelve backfilled contracts will have until their
     * partners sign.
     *
     * THE ARC AND THE TWO CURRENCY LIMBS ARE LOAD-BEARING, NOT PEDANTRY. A contract can hold the
     * PREVIOUS owner's executed record beside the current owner's open one — the composite key
     * admits both — so a seat read scoped to the CONTRACT lifts this on somebody else's signature;
     * and a partner with two cars, or a fee corrected past DRAFT where the void trigger is silent,
     * leaves a referent that satisfies the owner limb while `agreement_sign` refuses.
     */
    partnerScheduleRequired: boolean;
    /**
     * The partner has executed a Vehicle Schedule THIS CONTRACT CAN RELY ON — see above. The name
     * is about the outcome the gate reads, which is why `partnerScheduleLockedFor` tests one flag.
     */
    partnerScheduleSigned: boolean;
}

export interface BoardSlotInput {
    contractId: number;
    driverId: number | null;
    witnessId: number | null;
    /**
     * `contracts.cash_price` — what the vehicle could have been bought outright for.
     *
     * A FACT, NOT AN OPTION, and it is required here for the reason every other field on this
     * input is: a caller that could leave it out would generate an unstated cash price silently,
     * and the gate below would be a gate only on the surfaces that remembered to answer.
     *
     * TWO INSTRUMENTS STATE IT [D-B59]: the hire purchase agreement's Schedule, and the partner's
     * Vehicle Schedule, which prints it as the Cash price and measures every return against it.
     */
    cashPrice: number | null;
    /**
     * WHOSE VEHICLE THIS CONTRACT IS ABOUT — `vehicles.partner_id`, through `contracts.vehicle_id`.
     *
     * REQUIRED, ON `cashPrice`'S REASONING AND MORE SHARPLY [D-B48]. A caller that could leave it
     * out would silently report Primelane's own fleet: no schedule slot minted, and the hire
     * purchase agreement offered for signature on a partner's car whose owner has signed nothing —
     * which `agreement_sign` refuses. A gate only on the surfaces that remembered to answer is not
     * a gate.
     *
     * NULL IS "NOT READ YET", NEVER "THE HOUSE". The column is NOT NULL in the schema, so an
     * absent value means the contract names no vehicle or the row has not landed; `isThirdPartyVehicle`
     * answers false for it and nothing is minted, which is the same thing `agreement_create` does
     * with `AGREEMENT_SUBJECT_INCOMPLETE`.
     */
    vehiclePartnerId: number | null;
    /**
     * `contracts.vehicle_id` — WHICH car this contract is about.
     *
     * REQUIRED BESIDE `vehiclePartnerId`, and it is the second half of one question rather than a
     * decoration on it. `agreement_sign`'s gate asks that the referent be about the CURRENT vehicle
     * as well as the current owner, because a partner with two cars satisfies the owner limb on
     * either of them — and past DRAFT nothing moves the referent when the contract's vehicle does.
     * A board that read only the owner would lift the lock over a schedule signed about a different
     * car and offer to generate into `AGREEMENT_PRIOR_SIGNATURE_REQUIRED`.
     *
     * NULL IS "NO VEHICLE / NOT READ YET", exactly as `vehiclePartnerId`'s null is — and the two
     * are null together, so nothing is minted and no lock applies.
     */
    vehicleId: number | null;
    /**
     * `contracts.management_rate` — the per-payment percentage Primelane keeps.
     *
     * REQUIRED FOR THE SAME REASON, AND ZERO IS AS FATAL AS NULL. It is 0 on all 28 populated
     * production rows and NULL on 9 of the 17 external contracts, and `agreement_create` refuses
     * both: a Vehicle Schedule whose fee is nothing is not the bargain the instrument records.
     */
    managementRate: number | null;
    /** `contract_agreements`, keyed by its DRIVER/WITNESS type. */
    contractAgreements: Array<LegacyAgreementRow & { type: string }>;
    /**
     * The `contract_partner_agreements` row this board is about, already picked.
     *
     * THE PICK IS `boardSubjects.pickPartnerAgreement`, not this function's: a contract can hold a
     * previous owner's sealed row beside the current owner's, and choosing between them needs the
     * vehicle's partner — which the caller has and the read does not.
     *
     * OPTIONAL, AND ABSENT MEANS NO SCHEDULE HAS BEEN RAISED. Unlike the two fields above, the
     * omission here falls to the conservative answer rather than a permissive one: an empty slot
     * carrying no seal and no document, which is what a contract that has never generated one
     * genuinely looks like.
     */
    partnerAgreement?: PartnerAgreementRow | null;
    /**
     * The SETUP junction's `insurance_loans` — the first one the contract recorded [D-B17].
     *
     * AT `'all'` IT IS THE WHOLE RENEWAL CHAIN [D-B45], in junction order. The scope is settled
     * before this input is built (`boardSubjects.scopedJunctions`), so nothing here can tell which
     * board it is deriving — which is the property that lets one function serve both.
     */
    insuranceLoans: SubjectWithAgreement[];
    /**
     * THE CONTRACT'S STANDING OPERATIONAL EXPENSE ADVANCES — `loans` (owner's ruling, 2026-09-16).
     *
     * OPTIONAL, AND ABSENT MEANS THE SETUP CANVAS [D-B17]. Unlike `insuranceLoans`, the omission
     * here falls to the answer the setup board must keep: no LOAN slot at all. A driver's standing
     * advance is not part of setting a contract up — it is opened whenever they borrow, usually
     * long after the hire purchase agreement was signed — so a row for it on the setup manifest is
     * a row nobody can act on, and an instrument inside `RATE_INSTRUMENTS`' reach that could lock
     * clause 2.3 over a debt the contract was never written around. `boardSubjects` issues no read
     * for it at `'setup'`, so the omission and the empty array are the same fact arriving two ways.
     *
     * WHERE IT IS PRESENT, THE SLOT IS THE ADVANCE'S OWN DOOR. The Schedule is executed in the app
     * now, from Contract Insights, and `agreement_create('LOAN', <loans.id>)` is called with the
     * subject id this array carries.
     */
    loans?: SubjectWithAgreement[];
    deposits: SubjectWithAgreement[];
    documents: AgreementDocumentRow[];
    arcs: AgreementDocumentArcs[];
    /**
     * IS THE PAYMENT RECORD ACTUALLY PUBLISHED ON THIS DATABASE? — a PUBLISHED
     * `agreement_templates` row keyed `insurance_payment` [D-B45].
     *
     * THE ONE THING THE CLIENT CANNOT DERIVE, AND THE ONE IT MUST NOT ASSUME. Every other input
     * here is a fact about the contract; this is a fact about the REGISTRY, and it decides whether
     * `agreement_create` can raise a Record at all. These two apps and the migration that teaches
     * the server about the Record ship independently, so there is a real window in which the board
     * knows about an instrument the server does not — and in it, a slot named "Insurance Premium
     * Payment Record" leads an admin through a send confirmation naming that document and receives
     * an Insurance Premium Advance, whose clause 1.1 reads "The Borrower did not pay the whole of
     * it" over a driver who did. The mirror image is the countersign gate: `agreement_sign` would
     * still AND the witness limb, so a board offering the button on the driver's arm alone is
     * offering one the server refuses with `AGREEMENT_COUNTERSIGN_BLOCKED`.
     *
     * SO THE REGISTRY GATES BOTH HALVES, AND THE SERVER IS THE TRUTH IN BOTH WORLDS. Unpublished,
     * every INSURANCE_LOAN slot is an ADVANCE — the name this app has always used and the full
     * D-B27 gate it has always applied — and the moment the migration publishes the row the board
     * starts naming Records without anybody deploying anything.
     *
     * OPTIONAL, AND ABSENT MEANS UNPUBLISHED. Unlike `cashPrice`, the omission here fails to the
     * conservative answer rather than the permissive one: a caller that has not read the registry
     * gets exactly the behaviour that predates this work.
     */
    recordPublished?: boolean;
}

function money(amount: number | null | undefined): string | undefined {
    return amount === null || amount === undefined ? undefined : formatCurrency(amount);
}

function dated(value: string | null | undefined, prefix: string): string | undefined {
    return value ? `${prefix} ${format(new Date(value), 'MMM d, yyyy')}` : undefined;
}

/** Two places, which is what `contracts.management_rate` and `ROUND(x::numeric, 2)` both hold. */
const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * `agreement_create`'s three-way insurance selector, client-side [D-B57].
 *
 * THE SERVER'S CASE EXPRESSION (migration `20260920010000`, the
 * `p_doc_type = 'INSURANCE_LOAN'` arm): paid in full answers the Record [D-B45]; an advance with
 * NO instalment row, on a contract whose hire purchase agreement came back on paper, answers the
 * page settled at the end of the contract; everything else answers the ordinary Advance. The paid
 * limb is `instrumentFor`'s, passed in already decided rather than computed a second time here —
 * one predicate for the money, and this is the branch AROUND it.
 *
 * `masterOnPaper` IS `contract_hire_purchase_is_paper`, LIMB FOR LIMB WITH ONE SPELLING APART —
 * a signed `contract_agreements` DRIVER row whose `COALESCE(origin, 'SCANNED')` is `SCANNED`, AND
 * no live `agreement_documents` HIRE_PURCHASE row. See the derivation in `buildBoardSlots`, which
 * is the same two limbs [D-B53] and was already the server's own conjunction before this read it.
 * The spelling that differs is what "live" means: the server excludes VOIDED and SUPERSEDED, this
 * board admits PENDING_REVIEW, IN_SIGNING and COMPLETED, and those agree only while
 * `agreement_doc_status` has exactly its five values — see `isLiveDocument`.
 *
 * AN UNKNOWN ANSWERS NULL ON EVERY LIMB, and that is the whole difference between this and
 * `instrumentFor` beside it. The instrument falls back to ADVANCE because SOMETHING has to name a
 * row, and a wrong label costs a label; this one feeds a hint that ACCUSES a document — "generated
 * on the wrong page, use Void & reissue" — and void is a DELETE [D-B40]. So where the client cannot
 * reproduce the server's answer faithfully the row stays silent rather than tell an admin to
 * destroy a document the server raised correctly. There are three such places and they are checked
 * in the server's own order:
 *
 *   AN UNREAD INSTALMENT COUNT. `NOT EXISTS instalments` is the limb both of the server's branches
 *   turn on, and nothing else can stand in for it.
 *
 *   A PREMIUM AT OR BELOW THE HALF-PESEWA TOLERANCE, which is where the client's deposit sum and
 *   the server's part company outright. The server's `SUM(t.amount)` over NO deposit rows is NULL,
 *   `NULL >= amount - 0.005` is NULL and the COALESCE settles it false; the client's total over no
 *   rows is 0, and `0 >= amount - 0.005` is TRUE once the premium is 0.005 or less. One of them
 *   says Record and the other says Advance on the same loan, and which is which flips again with a
 *   single deposit row. Nothing live carries a premium that small — this is the guard being drawn
 *   where the two predicates PROVABLY diverge rather than where they are known to.
 *
 *   AND A DEPOSIT TOTAL NOBODY READ, on the one branch where it decides anything. `instrumentFor`
 *   reads an absent total as 0 and answers the advance, which is the right fallback for a LABEL and
 *   the wrong one here: a loan that is in fact deposited in full would have its valid Payment Record
 *   accused. Above a counted zero instalment the total decides nothing at all — see below — so the
 *   guard sits after that test and not before it.
 *
 * THE INSTALMENT TEST RUNS BEFORE THE MONEY, WHICH IS THE SERVER'S ORDER READ THE SHORT WAY. The
 * server's paid-in-full limb carries `AND NOT EXISTS instalments`, so an instalment row settles the
 * key as the ordinary Advance whatever the deposits add up to — production loan 203 sits at 5,000
 * deposited against 6,392 WITH a schedule. Asking the count first is therefore not a reordering of
 * the server's CASE but a reading of it, and it is what lets a financed advance keep its hint when
 * the deposit total is missing. `instrumentFor` can never answer PAYMENT_RECORD above a zero count,
 * so no branch below is reachable in a different order.
 *
 * AND A RECORD THE REGISTRY CANNOT RAISE ANSWERS NULL TOO. Where `insurance_payment` is unpublished
 * the money still reads paid-in-full and the SERVER still picks that key — and then refuses with
 * `AGREEMENT_TEMPLATE_MISSING`. Naming the Record would point at a document this database cannot
 * produce; naming either advance instead would be a DIFFERENT answer from the server's. Silence is
 * the only honest one, and it is the same gate `instrument` applies one line above the caller.
 *
 * ONE UNDER-READ IS KNOWINGLY UNGATED, and it is named here so the next reader does not assume
 * otherwise: `depositTotalOf` counts a deposit row whose transaction the embed did not return as
 * zero, which is indistinguishable from a loan with fewer deposits. It has no live producer —
 * `insurance_loan_deposits.transaction_id` is `bigint NOT NULL` behind an FK and `transactions` is
 * readable by every admin (`transactions_admin_only ... USING (is_admin())`) — so closing it would
 * mean carrying a row count through the subject read for a shape the database cannot express.
 */
function expectedInsurancePage(args: {
    instrumentByMoney: InsuranceInstrument;
    amount: number | null | undefined;
    depositTotal: number | null | undefined;
    instalmentCount: number | null | undefined;
    masterOnPaper: boolean;
    recordPublished: boolean;
}): string | null {
    const {
        instrumentByMoney,
        amount,
        depositTotal,
        instalmentCount,
        masterOnPaper,
        recordPublished,
    } = args;

    if (instalmentCount === null || instalmentCount === undefined) return null;
    // A schedule settles it without the money, and without the registry: `NOT EXISTS instalments`
    // fails both of the server's first two branches at once, so the answer is the ordinary Advance.
    if (instalmentCount > 0) return TEMPLATE_KEY_BY_INSTRUMENT.ADVANCE;

    // Below here the deposits decide, so the two limbs of that comparison have to be trustworthy.
    // A NULL amount is not one of these: `SUM >= NULL - 0.005` is NULL on the server and settles
    // false, which is `instrumentFor`'s answer too, so the two still agree.
    if (amount !== null && amount !== undefined && !(amount > FULL_DEPOSIT_TOLERANCE)) return null;
    if (depositTotal === null || depositTotal === undefined) return null;

    if (instrumentByMoney === 'PAYMENT_RECORD') {
        return recordPublished ? TEMPLATE_KEY_BY_INSTRUMENT.PAYMENT_RECORD : null;
    }
    return masterOnPaper ? INSURANCE_AT_END_TEMPLATE_KEY : TEMPLATE_KEY_BY_INSTRUMENT.ADVANCE;
}

export function buildBoardSlots(input: BoardSlotInput): BoardSlot[] {
    const arcById = new Map(input.arcs.map((arc) => [arc.id, arc]));
    const live = input.documents.filter((doc) => isLiveDocument(doc.status));

    const findDocument = (
        docType: AgreementDocType,
        arcColumn: keyof AgreementDocumentArcs,
        legacyRowId: number | null,
    ): AgreementDocumentRow | null => {
        if (legacyRowId === null) return null;
        return (
            live.find(
                (doc) =>
                    doc.doc_type === docType && arcById.get(doc.id)?.[arcColumn] === legacyRowId,
            ) ?? null
        );
    };

    /**
     * THE CROSS-DOCUMENT EVIDENCE, DERIVED HERE AND NOWHERE ELSE ON THIS LAYER.
     *
     * Both halves are the server's own predicate read off the rows this board already holds: a
     * SIGNED seat on the live document of that type (VOIDED and SUPERSEDED are not live, which is
     * what `live` above already enforces), or a `signed_at` on the legacy `contract_agreements`
     * row of that type. The legacy lookup names the TYPE as well as the contract for the reason
     * `agreement_sign` spells out: that table is keyed on the pair, and evidence about the witness
     * must never be read as evidence about the driver.
     */
    const legacySignedOf = (type: 'DRIVER' | 'WITNESS'): boolean =>
        !!input.contractAgreements.find((row) => row.type === type)?.signed_at;

    const seatSignedOn = (docType: AgreementDocType, party: string): boolean =>
        live.some(
            (doc) =>
                doc.doc_type === docType &&
                doc.signatures.some(
                    (signature) => signature.party === party && signature.status === 'SIGNED',
                ),
        );

    const driverSigned = seatSignedOn('HIRE_PURCHASE', 'DRIVER') || legacySignedOf('DRIVER');
    const witnessSigned = seatSignedOn('WITNESS', 'WITNESS') || legacySignedOf('WITNESS');

    /**
     * THE FOURTH CONTRACT FACT, AND THE SERVER'S OWN CONJUNCTION [D-B53] — see `masterOnPaper`.
     *
     * IT IS NOT `driverSigned && !document`. The driver arm accepts an IN_APP write-back on the
     * legacy row and a SIGNED seat either, and neither of those is a paper original; this asks the
     * narrower question `agreement_sign` asks, on the same two `contract_agreements` columns and
     * against the same absence.
     *
     * THE DOCUMENT HALF READS `live`, WHICH IS THIS BOARD'S READING OF `NOT IN ('VOIDED',
     * 'SUPERSEDED')` — the same set every other derivation on this layer is taken over, so a board
     * that called an agreement voided would call it voided everywhere at once. IT IS AN ALLOWLIST
     * WHERE THE SERVER WRITES A DENYLIST, and the two coincide only while the enum has its five
     * values: a sixth would read as not-live here and flip this conjunction on. That is
     * `isLiveDocument`'s shape across the whole board rather than this derivation's, and inverting
     * it belongs in a change of its own.
     */
    const masterOnPaper =
        !!input.contractAgreements.find(
            (row) =>
                row.type === 'DRIVER' &&
                !!row.signed_at &&
                (row.origin ?? 'SCANNED') === 'SCANNED',
        ) && !live.some((doc) => doc.doc_type === 'HIRE_PURCHASE');

    /**
     * THE THIRD CONTRACT FACT [D-B48], derived beside the other two and off the same rows.
     *
     * The referent is the schedule's legacy row, so its seal is read exactly as the DRIVER and
     * WITNESS rows' are. The seat half is NOT read the same way, and the two blocks below say why:
     * `contract_agreements` is unique per (contract, type) and `contract_partner_agreements` is
     * not, so the schedule's seat has to be read on the document arced to the picked referent
     * rather than on any live document of its type.
     */
    const partnerAgreement = input.partnerAgreement ?? null;
    const partnerScheduleRequired = isThirdPartyVehicle(input.vehiclePartnerId);

    /**
     * THE SCHEDULE'S OWN DOCUMENT, MATCHED BY ARC AND HOISTED HERE FOR THAT REASON.
     *
     * It used to be found inside the mint block below, and the seat was read off ANY live
     * `PARTNER_MANAGEMENT` document on the contract instead. That is a contract-scoped reading of
     * an arc-scoped fact, and the two come apart in exactly the case the referent family was built
     * for: `contract_partner_agreements_contract_partner_key` is UNIQUE `(contract_id, partner_id)`
     * so a vehicle that changes hands keeps the PREVIOUS owner's executed record beside the new
     * owner's open one, and the old one's document stays COMPLETED — which `LIVE_DOC_STATUSES`
     * admits. `pickPartnerAgreement` correctly hands over the new owner's row, and a
     * contract-scoped seat read would still find the old owner's signature and lift the lock over
     * a driver whose own signature `agreement_sign` then refuses.
     */
    const partnerDocument = findDocument(
        'PARTNER_MANAGEMENT',
        'contract_partner_agreement_id',
        partnerAgreement?.id ?? null,
    );

    /**
     * IS THE PICKED REFERENT STILL THE ONE THIS CONTRACT IS ABOUT? — the gate's two currency limbs.
     *
     * `agreement_sign` requires the schedule to be about the contract's CURRENT vehicle and to have
     * been raised at the fee the contract CURRENTLY charges (`20260908020000:1677-1684`), and both
     * limbs sit inside the same `NOT EXISTS` as the seal. Neither is redundant beside the owner
     * limb `pickPartnerAgreement` already applies: one partner can own two cars, and past DRAFT
     * `contracts_void_partner_schedule_on_money_change` withdraws nothing, so a contract whose
     * vehicle or fee moved there keeps a referent stating the old ones.
     *
     * THE RATE COMPARISON IS THE SERVER'S, INCLUDING ITS NULL ARM. `cpa.management_rate IS NULL`
     * satisfies the limb outright — a referent raised before the column carried a fee is not
     * superseded by one — and where it is stated the two are compared at two places, which is the
     * precision `contracts.management_rate` is stored at. A NULL contract rate can satisfy nothing,
     * which is the SQL's own answer: `ROUND(NULL) = ROUND(x)` is NULL, not true.
     *
     * THE SIBLING BOARD MODELS BOTH OFF THE SAME SQL LINES (`partners/partnerAgreements.ts`
     * `rateSuperseded` / `vehicleSuperseded`), and two admin surfaces disagreeing about one gate is
     * the failure this keeps shut.
     */
    const partnerScheduleCurrent =
        partnerAgreement !== null &&
        input.vehicleId !== null &&
        partnerAgreement.vehicle_id === input.vehicleId &&
        (partnerAgreement.management_rate === null ||
            (input.managementRate !== null &&
                round2(partnerAgreement.management_rate) === round2(input.managementRate)));

    /**
     * THE SERVER'S `NOT EXISTS`, CLIENT-SIDE: a referent about this car at this fee, carrying either
     * its own seal or a SIGNED PARTNER seat on the document arced to it.
     */
    const partnerScheduleSigned =
        partnerScheduleCurrent &&
        (!!partnerAgreement?.signed_at ||
            (partnerDocument?.signatures ?? []).some(
                (signature) => signature.party === 'PARTNER' && signature.status === 'SIGNED',
            ));

    const slots: BoardSlot[] = [];

    // ── The vehicle's owner, before anything the driver signs ────────────────
    /**
     * THE SCHEDULE IS THE FIRST ROW ON THE CANVAS, AND THE ORDER IS THE RULE ITSELF [D-B48].
     *
     * No driver signs a hire purchase agreement before the vehicle's partner has signed the
     * schedule about that vehicle, so the instrument that has to be executed first is the one an
     * admin meets first. `slots` is walked in push order by every surface that draws it.
     *
     * MINTED ONLY ON A THIRD-PARTY VEHICLE. Primelane's own fleet is 259 of 289 vehicles and
     * `agreement_create` refuses a schedule on one of them by name (`AGREEMENT_PARTNER_IS_HOUSE`),
     * so a slot there would be a row whose only outcome is a toast. The house is not a badge and
     * not an optional row: it is a contract this instrument is not about.
     *
     * ITS SUBJECT IS THE CONTRACT, WHICH IT SHARES WITH THE OTHER TWO ARCS. `slotKey` carries the
     * doc type as well for exactly this reason — three rows, one subject id — so a pending spinner
     * and a React key still land on the row that was clicked.
     */
    if (partnerScheduleRequired) {
        /**
         * THE FEE HAS TO BE STATED, AND `<= 0` IS LOAD-BEARING RATHER THAN DEFENSIVE.
         *
         * `agreement_create` refuses a NULL rate AND a zero one with `AGREEMENT_SUBJECT_INCOMPLETE`
         * — a schedule whose management fee is nothing is not the bargain this instrument records
         * — and zero is the LIVE VALUE on all 28 populated production rows. A block written as
         * `== null` would leave every one of them offering Generate into a refusal.
         *
         * IT LIFTS ON THE CASH PRICE BLOCK'S TWO CONDITIONS BELOW, WHERE NOTHING IS LEFT TO
         * GENERATE: a live document has frozen its own fee onto the referent, and a sealed referent
         * (a schedule signed on paper) carries the fee it was signed at, frozen at the partner's
         * signature. The rate moving underneath either is the gate's business (`agreement_sign`
         * compares the two), not a precondition of minting, and blocking either would paint a
         * signed schedule destructive over a fee it no longer needs.
         */
        const rateBlock =
            partnerDocument ||
            partnerAgreement?.signed_at ||
            (input.managementRate !== null && input.managementRate > 0)
                ? null
                : {
                      reason: 'State the management rate before generating this document.',
                      step: 'financials',
                  };

        /**
         * AND THE CASH PRICE HAS TO BE STATED, BEHIND THE FEE [D-B59].
         *
         * `agreement_create` copies `round(contracts.cash_price, 2)` onto the record, refuses a
         * NULL or non-positive one with `AGREEMENT_CASH_PRICE_REQUIRED`, and the column it copies
         * into is `CHECK (all_in_cost > 0)`: the schedule prints it as the Cash price and divides
         * the net return, the annualised return and the recovery period by it. The test is
         * `partnerScheduleCashPriceRefusal`, the rule the generate dialog holds Generate shut on,
         * so the row and the dialog say one sentence: zero, and anything that rounds to 0.00, is
         * refused as well as NULL. That is where this differs from the hire purchase agreement's
         * `cashPriceBlockFor` below (that one generates without a figure and reports the gap
         * afterwards).
         *
         * THE FEE'S BLOCK WINS WHEN BOTH ARE MISSING. Only one sentence can be shown, both are
         * cleared on Financials, and the fee was this row's precondition first.
         *
         * IT LIFTS WHERE NOTHING IS LEFT TO GENERATE. A live document has already copied its
         * figure, and a sealed referent (a schedule signed on paper, whose `signed_at` is the
         * row's `legacySignedAt`) is executed: `agreement_create` never writes the column on a
         * sealed slot. Blocking either would paint a signed schedule destructive over a figure
         * it no longer needs.
         */
        const cashPriceRefusal =
            partnerDocument || partnerAgreement?.signed_at
                ? null
                : partnerScheduleCashPriceRefusal(input.cashPrice);
        const cashPriceBlock = cashPriceRefusal ? { reason: cashPriceRefusal, step: 'financials' } : null;
        const scheduleBlock = rateBlock ?? cashPriceBlock;

        slots.push({
            key: slotKey('PARTNER_MANAGEMENT', input.contractId),
            docType: 'PARTNER_MANAGEMENT',
            subjectId: input.contractId,
            title: partnerDocument?.title ?? DOC_TYPES.PARTNER_MANAGEMENT.label,
            instrument: null,
            expectedTemplateKey: null,
            legacyRowId: partnerAgreement?.id ?? null,
            legacySignedAt: partnerAgreement?.signed_at ?? null,
            legacyUrl: partnerAgreement?.agreement_url ?? null,
            legacyOrigin: partnerAgreement?.origin ?? null,
            cashPrice: input.cashPrice,
            document: partnerDocument,
            blockedReason: scheduleBlock?.reason ?? null,
            blockedStep: scheduleBlock?.step ?? null,
            driverSigned,
            witnessSigned,
            masterOnPaper,
            partnerScheduleRequired,
            partnerScheduleSigned,
        });
    }

    // ── The two contract arcs ────────────────────────────────────────────────
    /**
     * THE PRECONDITION SPLITS BY DOCUMENT [D-B18]. The hire purchase agreement seats the driver and,
     * since the witness came off it, prints nobody else — so demanding a witness before it can be
     * generated would invert the ruled sequence, holding the driver's own instrument shut until
     * somebody had entered a person whose instrument is not even available until the driver signs.
     * The witness agreement is addressed to the witness and names the driver in its text, so it
     * needs both. This is `agreement_create`'s rule, client-side.
     */
    const partyBlockFor = (
        docType: 'HIRE_PURCHASE' | 'WITNESS',
    ): { reason: string; step: string } | null => {
        if (!input.driverId) return { reason: 'This contract has no driver yet.', step: 'driver' };
        if (docType === 'WITNESS' && !input.witnessId) {
            return { reason: 'Choose a witness before generating this document.', step: 'agreements' };
        }
        return null;
    };

    /**
     * THE HIRE PURCHASE AGREEMENT ALSO NEEDS A CASH PRICE, AND ONLY THAT ONE DOES.
     *
     * The cash price is the first row of the Schedule's particulars, and the difference between it
     * and the hire-purchase price beside it is the charge for credit — which is the thing a hire
     * purchase agreement has to state before it is made. Generating without it does not fail: it
     * mints a document whose Schedule carries an unfilled figure, reported afterwards by the merge
     * pre-flight, on a page an admin is one confirm away from sending out.
     *
     * NULL IS THE WHOLE TEST, and it is the column's own meaning rather than a proxy for one:
     * `contracts.cash_price` is nullable with no default precisely so that "nobody has stated one"
     * is distinguishable from any figure, and `agreement_merge_data` reads it straight rather than
     * falling back to `sold_at` for the same reason (`20260816010000`).
     *
     * NO OTHER CONTRACT ARC IS GATED HERE. The witness agreement, the deposit agreement and the
     * insurance advance print no cash price at all, and blocking them on one would hold up papers
     * that have nothing to say about it. The partner's Vehicle Schedule does state it, and is gated
     * in its own branch above, where zero is refused as well [D-B59].
     */
    const cashPriceBlockFor = (
        docType: 'HIRE_PURCHASE' | 'WITNESS',
    ): { reason: string; step: string } | null => {
        if (docType !== 'HIRE_PURCHASE' || input.cashPrice !== null) return null;
        return {
            reason: 'State the cash price before generating this document.',
            step: 'financials',
        };
    };

    for (const docType of ['HIRE_PURCHASE', 'WITNESS'] as const) {
        const legacyType = docType === 'HIRE_PURCHASE' ? 'DRIVER' : 'WITNESS';
        const legacy = input.contractAgreements.find((row) => row.type === legacyType) ?? null;
        const document = findDocument(docType, 'contract_agreement_id', legacy?.id ?? null);
        // Only blocks generation. An existing document already froze its parties and its figures.
        // THE PARTY COMES FIRST when both are missing: an instrument addressed to nobody is the
        // more fundamental gap, and the driver area is where an admin lands anyway.
        const block = document ? null : (partyBlockFor(docType) ?? cashPriceBlockFor(docType));

        const slot: BoardSlot = {
            key: slotKey(docType, input.contractId),
            docType,
            subjectId: input.contractId,
            title: document?.title ?? DOC_TYPES[docType].label,
            instrument: null,
            expectedTemplateKey: null,
            legacyRowId: legacy?.id ?? null,
            legacySignedAt: legacy?.signed_at ?? null,
            legacyUrl: legacy?.agreement_url ?? null,
            legacyOrigin: legacy?.origin ?? null,
            cashPrice: null,
            document,
            blockedReason: block?.reason ?? null,
            blockedStep: block?.step ?? null,
            driverSigned,
            witnessSigned,
            masterOnPaper,
            partnerScheduleRequired,
            partnerScheduleSigned,
        };

        /**
         * THE PARTNER SIGNS BEFORE THE DRIVER DOES [D-B48] — the advance's lock, on the master
         * instrument, held by the instrument above it.
         *
         * IT LOCKS GENERATION AND NOT MERELY THE SEND, which is `advanceLockedFor`'s own ruling
         * ("locked until the driver has signed, not offered and then refused") applied one step
         * earlier in the same sequence: a hire purchase agreement generated before the partner has
         * signed lands in the driver's inbox as a document `agreement_sign` refuses, and the driver
         * cannot see the partner's paperwork to know why.
         *
         * IT YIELDS TO A BLOCK THIS SLOT ALREADY CARRIES, for the reason the party gate outranks
         * the cash price a few lines above: an instrument addressed to nobody, or priced at
         * nothing, is the more fundamental gap and only one sentence can be shown. Both have to be
         * cleared either way, and neither is reachable past the other.
         *
         * AND IT CARRIES NO `blockedStep`, WHICH IS THE ONE PLACE THIS BLOCK DIFFERS FROM THE OTHERS.
         * Every other reason on this board names an area the admin is NOT on — `financials` for the
         * cash price and the fee, `driver` for the party — and the row renders it as a
         * "Resolve in …" button. The schedule is drawn on the agreements canvas, directly above
         * this row, so the same button would read "Resolve in Agreements" and navigate to the page
         * it was pressed on: a dead end, and a duplicate history entry with it. The lock line above
         * the row says what is owed and the row that takes it is already on screen.
         */
        if (!slot.blockedReason && partnerScheduleLockedFor(slot)) {
            slot.blockedReason = PARTNER_SCHEDULE_LOCK_REASON;
            slot.blockedStep = null;
        }

        slots.push(slot);
    }

    // ── Insurance loans, loans, deposits ─────────────────────────────────────
    const multiple = (list: SubjectWithAgreement[], index: number) =>
        list.length > 1 ? `#${index + 1}` : undefined;

    input.insuranceLoans.forEach((subject, index) => {
        // THE INSTRUMENT IS DERIVED FROM THE SUBJECT, ONCE, AND THE TITLE PREFERS THE DOCUMENT
        // [D-B45]. A generated document already carries the title of the template the SERVER
        // picked, so on a row that has one the client predicate never gets to name anything.
        //
        // The three limbs are spelled out rather than spread: `instrumentFor` takes them all as
        // required, so a subject read that stopped carrying one is a type error here rather than
        // a slot that silently reverts to naming everything an advance.
        //
        // AND THE REGISTRY IS ASKED FIRST. A Record the server cannot raise is a Record this board
        // must not name or gate for — see `recordPublished`. Where it is unpublished the money is
        // not consulted at all and every insurance slot is an advance, which is what this board
        // said before the instrument existed.
        const instrumentByMoney = instrumentFor({
            amount: subject.amount ?? null,
            depositTotal: subject.depositTotal ?? null,
            instalmentCount: subject.instalmentCount ?? null,
        });
        const instrument: InsuranceInstrument = input.recordPublished
            ? instrumentByMoney
            : 'ADVANCE';
        // AND THE PAGE BESIDE THE INSTRUMENT [D-B57], because the two advances share a title and a
        // value and the row still has to tell them apart. Same rows, same registry gate; see
        // `expectedInsurancePage` for why an unread count and an unpublished Record both answer null.
        const expectedTemplateKey = expectedInsurancePage({
            instrumentByMoney,
            amount: subject.amount,
            depositTotal: subject.depositTotal,
            instalmentCount: subject.instalmentCount,
            masterOnPaper,
            recordPublished: !!input.recordPublished,
        });
        const document = findDocument(
            'INSURANCE_LOAN',
            'insurance_loan_agreement_id',
            subject.agreement?.id ?? null,
        );
        const slot: BoardSlot = {
            key: slotKey('INSURANCE_LOAN', subject.id),
            docType: 'INSURANCE_LOAN',
            subjectId: subject.id,
            title: document?.title ?? INSTRUMENT_TITLES[instrument],
            instrument,
            expectedTemplateKey,
            label: multiple(input.insuranceLoans, index),
            subtitle: money(subject.amount),
            legacyRowId: subject.agreement?.id ?? null,
            legacySignedAt: subject.agreement?.signed_at ?? null,
            legacyUrl: subject.agreement?.agreement_url ?? null,
            legacyOrigin: subject.agreement?.origin ?? null,
            cashPrice: null,
            document,
            blockedReason: null,
            blockedStep: null,
            driverSigned,
            witnessSigned,
            masterOnPaper,
            partnerScheduleRequired,
            partnerScheduleSigned,
        };

        // THE ADVANCE WAITS ON THE MASTER INSTRUMENT'S SIGNATURE (owner's ruling, 2026-08-18),
        // exactly as the witness agreement does. Derived from the slot itself so the board, the
        // canvas and the manifest are three readings of one rule rather than three copies of it —
        // see `advanceLockedFor`.
        if (advanceLockedFor(slot)) {
            slot.blockedReason = ADVANCE_LOCK_REASON;
            slot.blockedStep = 'agreements';
        }

        slots.push(slot);
    });

    /**
     * THE STANDING ADVANCES, MINTED ONLY WHERE A CALLER SUPPLIED THEM (owner's ruling, 2026-09-16).
     *
     * IT IS THE INSURANCE MINT'S SHAPE, DELIBERATELY AND DOWN TO THE FIELD ORDER. The two advances
     * are the same ceremony on two subjects: a BORROWER seat and Primelane's, a Schedule quoting
     * the master instrument's reference, a legacy row that carries the paper original and the seal
     * the finalize writes back. What differs is the table the subject lives in and the arc column
     * the document is matched by — so a difference anywhere else in this block would be a
     * difference nobody decided.
     *
     * NO INSTRUMENT AND NO CASH PRICE. `instrument` is the insurance type's question alone
     * [D-B45] — one `doc_type` there publishes two titles — and `LOAN` publishes exactly one, so
     * the row is named by the document it carries else by the type's label, which is the
     * precedence every other type keeps. `cashPrice` is the Vehicle Schedule's [D-B59].
     *
     * AND THE LOCK IS THE ADVANCE'S OWN [D-B55]. `advanceLockedFor` holds both advances shut until the
     * driver has signed the hire purchase agreement — see it for why one predicate answers for two
     * doc types.
     *
     * `stepForDocType('LOAN')` STILL ANSWERS `agreements`, and that is unchanged rather than
     * relied on here: the setup canvas is not fed `loans`, so no LOAN slot reaches its partition at
     * all, and totality stays a claim about the TYPE rather than about what a contract carries.
     */
    (input.loans ?? []).forEach((subject, index) => {
        const document = findDocument('LOAN', 'loan_agreement_id', subject.agreement?.id ?? null);
        const slot: BoardSlot = {
            key: slotKey('LOAN', subject.id),
            docType: 'LOAN',
            subjectId: subject.id,
            title: document?.title ?? DOC_TYPES.LOAN.label,
            instrument: null,
            expectedTemplateKey: null,
            label: multiple(input.loans ?? [], index),
            subtitle: money(subject.amount),
            legacyRowId: subject.agreement?.id ?? null,
            legacySignedAt: subject.agreement?.signed_at ?? null,
            legacyUrl: subject.agreement?.agreement_url ?? null,
            legacyOrigin: subject.agreement?.origin ?? null,
            cashPrice: null,
            document,
            blockedReason: null,
            blockedStep: null,
            driverSigned,
            witnessSigned,
            masterOnPaper,
            partnerScheduleRequired,
            partnerScheduleSigned,
        };

        if (advanceLockedFor(slot)) {
            slot.blockedReason = ADVANCE_LOCK_REASON;
            slot.blockedStep = 'agreements';
        }

        slots.push(slot);
    });

    input.deposits.forEach((subject, index) => {
        const parts = [money(subject.amount), dated(subject.dated, 'paid')].filter(Boolean);
        const document = findDocument(
            'DEPOSIT',
            'contract_deposit_agreement_id',
            subject.agreement?.id ?? null,
        );
        slots.push({
            key: slotKey('DEPOSIT', subject.id),
            docType: 'DEPOSIT',
            subjectId: subject.id,
            title: document?.title ?? DOC_TYPES.DEPOSIT.label,
            instrument: null,
            expectedTemplateKey: null,
            label: multiple(input.deposits, index),
            subtitle: parts.join(' · ') || undefined,
            legacyRowId: subject.agreement?.id ?? null,
            legacySignedAt: subject.agreement?.signed_at ?? null,
            legacyUrl: subject.agreement?.agreement_url ?? null,
            legacyOrigin: subject.agreement?.origin ?? null,
            cashPrice: null,
            document,
            blockedReason: null,
            blockedStep: null,
            driverSigned,
            witnessSigned,
            masterOnPaper,
            partnerScheduleRequired,
            partnerScheduleSigned,
        });
    });

    return slots;
}

// ─── The cross-document countersign gate [D-B18] ─────────────────────────────

/**
 * Whether Primelane may countersign THIS document right now.
 *
 * THE CROSS-DOCUMENT TERM IS THE POINT. Every other seat on the board is an intra-document order —
 * this one is a rule ACROSS instruments, and `agreement_sign` enforces it for `HIRE_PURCHASE`,
 * `WITNESS` and both advances alike: the driver must have signed the hire purchase agreement AND
 * the witness must have signed the witness agreement before any of them can be countersigned.
 * A row that asked only about its own seats would offer a button whose only outcome is
 * `AGREEMENT_COUNTERSIGN_BLOCKED`.
 *
 * ON THE TWO CONTRACT INSTRUMENTS ONLY THE SIBLING'S ARM IS CONSULTED, AND THAT IS THE WHOLE RULE,
 * NOT HALF OF IT: their own counterparties ARE their own arm. On the hire purchase agreement the
 * DRIVER seat being SIGNED is the driver arm; on the witness agreement the WITNESS seat is the
 * witness arm. So "every counterparty here has signed" plus "the other instrument's party has
 * signed" is exactly the server's conjunction. (A legacy three-seat hire purchase document —
 * template v2, contract 307 — carries a WITNESS seat of its own, and the server does NOT accept it
 * as the witness arm; neither does this, because `witnessSigned` only ever reads a WITNESS-type
 * document or the legacy WITNESS row.)
 *
 * ON THE ADVANCE BOTH ARMS ARE SIBLINGS, because neither is its own: its seats are BORROWER and
 * COMPANY_REP, and a signed borrower is evidence about the advance and about nothing else. See
 * `siblingSignatureOutstanding`.
 *
 * ON THE PAYMENT RECORD ONLY THE DRIVER'S ARM IS ASKED [D-B45] — the same carve-out
 * `agreement_sign` takes, keyed on the same fact. See `siblingSignatureOutstanding`.
 *
 * THE OPERATIONAL EXPENSE ADVANCE IS GATED EXACTLY LIKE THE PREMIUM ONE (owner's ruling,
 * 2026-09-16). It used to be gated nowhere, on an August premise — "its Schedule quotes no hire
 * purchase reference" — that was already false when it was written: loan v7 quotes the reference
 * three times. Both advances are a second debt papered against the master bargain, so Primelane's
 * last signature waits on the same two arms; the server arm lands with it, and the two halves have
 * to be in one release wave or this board withholds a button the server accepts.
 *
 * `DEPOSIT` IS STILL GATED NOWHERE, here or in SQL: it receipts money the driver has already paid
 * and creates no exposure to hold a signature over.
 *
 * SO IS THE VEHICLE SCHEDULE, AND ITS ABSENCE FROM THIS GATE IS A DECISION [D-B48]: [D-B27]'s
 * conjunction names the hire purchase agreement, the witness agreement and the advances, and the
 * schedule is executed BEFORE any of them exists. Its own counterparty is its own arm, there
 * is no sibling instrument it waits on, and the dependency runs the other way — it is what the
 * hire purchase agreement waits for (`partnerScheduleLockedFor`).
 */
export function countersignReadyFor(slot: BoardSlot): boolean {
    const doc = slot.document;
    if (!doc || doc.status !== 'IN_SIGNING') return false;

    const signatures = doc.signatures;
    const company = signatures.find((signature) => signature.party === 'COMPANY_REP');
    if (company?.status === 'SIGNED') return false;

    const counterparties = signatures.filter((signature) => signature.party !== 'COMPANY_REP');
    if (counterparties.length === 0) return false;
    if (!counterparties.every((signature) => signature.status === 'SIGNED')) return false;

    return !siblingSignatureOutstanding(slot);
}

/**
 * Whether a signature this document's own seats cannot supply is still outstanding — the server's
 * conjunction, minus whichever half this document answers for itself.
 *
 * THE TWO CONTRACT INSTRUMENTS EACH HAVE ONE SIBLING. Their own counterparty is one arm of the
 * conjunction, so only the other one is asked, and for a document whose own seats are all signed
 * exactly one arm can ever be outstanding.
 *
 * AN ADVANCE HAS TWO, AND THAT IS WHY IT CANNOT BE FOLDED INTO EITHER LINE ABOVE [D-B27]. Both
 * advance documents seat BORROWER and COMPANY_REP: the borrower's signature is evidence about the
 * advance and about neither contract instrument, so BOTH arms of the server's conjunction are
 * cross-document here and both have to be asked. They read the same two fields the other rows read
 * — they are contract facts hung on every slot — and answer true while either is missing, which is
 * exactly when `agreement_sign` raises `AGREEMENT_COUNTERSIGN_BLOCKED`.
 *
 * THE OPERATIONAL EXPENSE ADVANCE IS THE PREMIUM ADVANCE'S ARM FOR ARM (owner's ruling,
 * 2026-09-16), and it carries no carve-out of its own: the Payment Record's exists because nothing
 * is advanced under it, and an operational expense advance advances money by definition. It is a
 * separate line rather than a second label on the insurance line because the instrument question
 * is the insurance type's alone — `slot.instrument` is null on every LOAN row.
 *
 * THE DRIVER'S ARM IS NOT REDUNDANT ON THE ADVANCE, though it looks it: `advanceLockedFor` and the
 * server's own [D-B26] gate hold the borrower's FIRST signature until the driver has signed, so an
 * advance with a signed borrower had one. Voiding and reissuing the hire purchase agreement takes
 * that signature away again underneath an advance already in flight, and the countersignature is
 * then refused on the driver's arm alone.
 *
 * THE PAYMENT RECORD DROPS THE WITNESS ARM AND KEEPS THE DRIVER'S [D-B45], WHICH IS THE SERVER'S
 * OWN CARVE-OUT AND NOT A LOOSENING OF IT. `agreement_sign` skips the witness limb on a document
 * whose `template_key` is `insurance_payment`, because D-B27's rationale is the credit exposure a
 * third bargain creates and the Record creates none: nothing is advanced, no interest arises and
 * nothing is owed under it. Without the carve-out its last signature is unreachable on the 63 live
 * contracts carrying no recorded witness signature — every one of the 39 multi-junction contracts
 * among them. The client half has to land in the same release wave as the server's, or the board
 * goes on withholding a button the server would now accept.
 *
 * AND BOTH ADVANCES DROP THE WITNESS ARM WHERE THE MASTER IS ON PAPER [D-B53], WHICH IS THE
 * SERVER'S SECOND CARVE-OUT AND NOT A LOOSENING OF IT EITHER. `agreement_sign` skips the witness
 * limb — that limb alone, on the two advances only — where the contract carries a paper-origin
 * signed DRIVER row and no live hire purchase document. A witness agreement is the attestation to
 * an IN-APP driver signature and will never be generated for one executed offline, so without the
 * waiver Primelane's last signature on an advance is unreachable on 174 of the 232 paper-era
 * contracts: the money moves, the driver signs, and nobody can countersign. `masterOnPaper` is that
 * conjunction, derived in `buildBoardSlots` off the same two sources the server reads.
 *
 * ITS CLIENT HALF LANDS IN THE SAME RELEASE WAVE AS THE SERVER'S, exactly as the Record's did, and
 * the promote order is what keeps them together: fleet-backend goes first, so by the time this
 * board is live `agreement_sign` is already accepting the button it now offers. THE DRIVER'S ARM IS
 * UNTOUCHED BY IT — on these contracts that arm is satisfied by the very row this waiver reads, and
 * an advance whose driver has signed nothing is still held by the first limb.
 *
 * IT READS THE SLOT'S INSTRUMENT, WHICH IS THE SUBJECT'S SHAPE. Where that disagrees with a stale
 * document the server refuses the signature outright with `AGREEMENT_INSTRUMENT_MISMATCH` — a
 * refusal that names the real problem, which "the witness has to sign" would not.
 */
export function siblingSignatureOutstanding(slot: BoardSlot): boolean {
    if (slot.docType === 'HIRE_PURCHASE') return !slot.witnessSigned;
    if (slot.docType === 'WITNESS') return !slot.driverSigned;
    const witnessOutstanding = !slot.witnessSigned && !slot.masterOnPaper;
    if (slot.docType === 'INSURANCE_LOAN') {
        if (slot.instrument === 'PAYMENT_RECORD') return !slot.driverSigned;
        return !slot.driverSigned || witnessOutstanding;
    }
    if (slot.docType === 'LOAN') return !slot.driverSigned || witnessOutstanding;
    return false;
}

/**
 * Whether the board may offer the witness's signing link on THIS document.
 *
 * THE CROSS-DOCUMENT TERM IS INSIDE THIS ONE TOO [D-B18], for the reason it is inside
 * `countersignReadyFor`: `agreement_issue_token` refuses a WITNESS link on the witness agreement
 * until the driver has signed the hire purchase agreement — in the app, or on the legacy row a paper
 * original was typed onto — because the witness holds the FIRST seat there and what they attest to
 * is on the other instrument. Without the term the button paints for the whole window before the
 * lazy `agreement_render` read resolves `missing[]`, and a click inside that window lands on this
 * page's own "open Review first" refusal, which names the wrong problem entirely.
 *
 * THE TERM IS SCOPED TO THE WITNESS AGREEMENT, AND THAT SCOPE IS THE SERVER'S. Anywhere else the
 * witness seat is not the first one, so `agreement_issue_token` asks an INTRA-document question
 * instead — has some earlier seat signed — and an unscoped sibling term would withhold the link on a
 * legacy three-seat hire purchase document (template v2, contract 307) that the server would issue
 * for, on the strength of a witness agreement that contract is never going to have.
 *
 * `blockedByMerge` is `agreement_render`'s `missing[]`, which exists only once the document does.
 */
export function witnessLinkReadyFor(slot: BoardSlot, blockedByMerge: boolean): boolean {
    const witness = slot.document?.signatures.find((signature) => signature.party === 'WITNESS');
    if (!witness) return false;
    if (witness.status === 'SIGNED' || witness.status === 'VOIDED') return false;
    if (blockedByMerge) return false;
    return !(slot.docType === 'WITNESS' && siblingSignatureOutstanding(slot));
}

/**
 * Whether the row may draw the merge pre-flight — the list of fields the wording cannot resolve.
 *
 * TWO THINGS OUTRANK IT, AND BOTH FOR ONE REASON: only one explanation can be shown, so it has to
 * be the CAUSE rather than the symptom.
 *
 * `blockedReason` IS THE FIRST, and it is the older of the two. A slot held shut by a precondition
 * is being told the first thing that has to happen; a red "1 merge field is still empty" beneath a
 * padlock names a consequence of it.
 *
 * THE PAGE GAP IS THE SECOND [D-B57], and it is the one this predicate was extracted for. A
 * document generated on the ordinary Advance page, on a loan that has since turned out to owe no
 * weekly repayment of its own, reports the three weekly fields as unresolved — `first_instalment_date`,
 * `term_weeks`, `weekly_instalment` — and the pre-flight's "Resolve in Insurance" links would send
 * an admin to GIVE the loan a weekly instalment: a change to the money, made to satisfy a document,
 * on a contract where a weekly is optional. The page is what is wrong, void-and-reissue is the fix,
 * and the fields resolve themselves on the page the server would now pick.
 *
 * `blockedByMerge` is `agreement_render`'s `missing[]`, which exists only once the document does;
 * `instrumentGap` is whether the row was handed one, and a row that reads no gap passes false.
 */
export function mergePreflightShownFor(
    slot: BoardSlot,
    state: { blockedByMerge: boolean; instrumentGap: boolean },
): boolean {
    if (!slot.document) return false;
    if (!state.blockedByMerge) return false;
    return !slot.blockedReason && !state.instrumentGap;
}

/**
 * Whether the row may draw the page hint — the sentence naming the page its subject now calls for.
 *
 * THE LOCK OUTRANKS IT, EXACTLY AS IT OUTRANKS THE PRE-FLIGHT, and for the same reason: a slot held
 * shut by `blockedReason` is being told the FIRST thing that has to happen, and the status line
 * above already puts that sentence first. The combination is reachable rather than theoretical —
 * `advanceLockedFor` re-locks an unsigned INSURANCE_LOAN slot the moment `driverSigned` goes false,
 * which is what voiding and reissuing the hire purchase agreement does, and raising an IN_APP master
 * on a contract whose original was paper flips `masterOnPaper` false and opens this gap in the same
 * move. A padlock reading "The driver has to sign the hire purchase agreement first." over an amber
 * box telling an admin to void and reissue a DIFFERENT document is two first-things-to-do on one
 * row, which is the shape this file's predicates exist to prevent.
 *
 * THE HINT IS NOT LOST, ONLY DEFERRED. Nothing about the page changes while the lock holds, and the
 * document cannot be sent or signed through it either way — so the sentence is waiting for the row
 * to have one thing to say, not being withheld.
 *
 * `hasGap` is whether the row was handed a gap at all; a row that reads none passes false.
 */
export function pageGapShownFor(slot: BoardSlot, hasGap: boolean): boolean {
    if (!hasGap) return false;
    return !slot.blockedReason;
}

// ─── The premium advance's own dependency on the master instrument ───────────

/**
 * Why an advance cannot be started yet. The witness's sentence, on the satellites.
 *
 * ONE STRING FOR BOTH ADVANCES, AND IT NEEDED NO REWORDING TO COVER THE SECOND (owner's ruling,
 * 2026-09-16): it names the instrument that has to be signed rather than the one that is waiting,
 * and that is the hire purchase agreement either way.
 *
 * IT IS ALSO WHAT THE ROW SAYS AT THE OTHER END OF THE SAME DEPENDENCY [D-B27]: `advanceLockedFor`
 * draws it as a padlock before the advance is minted, and `AgreementDocumentRow` says it again when
 * the countersignature is held on the driver's arm — one fact, one wording, whichever end an admin
 * meets first.
 */
export const ADVANCE_LOCK_REASON = 'The driver has to sign the hire purchase agreement first.';

/**
 * Whether the insurance premium advance is still held shut by the hire purchase agreement.
 *
 * THE ADVANCE IS PRESENTED AS THE WITNESS AGREEMENT IS (owner's ruling, 2026-08-18): locked until
 * the driver has signed, not offered and then refused by a red merge-field box. Both satellites are
 * made IN CONNECTION WITH the master instrument and both quote its reference — the advance's
 * Schedule opens "It is made in connection with Hire Purchase Agreement no.
 * {{contract.hire_purchase_reference}} between the same Parties" — so an advance sent before that
 * agreement is signed is a second debt papered against a bargain nobody has struck.
 *
 * IT HOLDS BOTH INSURANCE INSTRUMENTS, AND THE NAME IS THE ONLY THING THAT IS NARROWER [D-B45].
 * The Payment Record is made in connection with the same master and names the same reference, and
 * [D-B26] holds the borrower's first signature on it exactly as on an advance — so the FIRST move
 * is gated identically. What the Record drops is the WITNESS arm of the countersign gate, which is
 * the other end of the ceremony and a different rule (`siblingSignatureOutstanding`).
 *
 * AND IT HOLDS THE OPERATIONAL EXPENSE ADVANCE ON THE SAME SENTENCE (owner's ruling, 2026-09-16).
 * Its Schedule opens with the same clause and quotes `{{contract.hire_purchase_reference}}` three
 * times, so an advance sent before the master is signed is the same second debt papered against a
 * bargain nobody has struck — one rule, one predicate, and `ADVANCE_LOCK_REASON` reads true of
 * both without a word changing: what it names is the hire purchase agreement, not the advance.
 *
 * THE AUGUST EXCLUSION OF `LOAN` RESTED ON A PREMISE THAT WAS ALREADY FALSE. It read "its Schedule
 * quotes no hire purchase reference", and loan v7 — v6 at the time — had quoted it since
 * `20260813020000`. The ruling supersedes it rather than the other way round.
 *
 * THE PREDICATE IS THE WITNESS'S OWN, AND DELIBERATELY THE SAME ONE: `driverSigned`, which
 * `buildBoardSlots` derives from a SIGNED DRIVER seat on the live hire purchase document OR the
 * legacy `contract_agreements` DRIVER row — so a paper original recorded on the master instrument
 * opens this exactly as it opens the witness agreement, which is the only thing that could ever open
 * it for the 174 paper-era contracts.
 *
 * IT IS A GATE ON STARTING, AND ONLY ON STARTING — `witnessRow`'s rule, applied to the other
 * satellite. Once this advance carries a signature of its own (a signed counterparty seat, or a
 * paper original on its own legacy row) the lock lifts whatever the master instrument is doing:
 * re-locking a document a party has already signed would hide their signature behind a padlock, and
 * that shape is real — voiding and reissuing the hire purchase agreement takes the driver's
 * signature away again.
 *
 * THE COUNTERSIGN RULE IS THE OTHER END OF THE SAME DEPENDENCY, AND THIS IS NOT IT [D-B27]. That
 * one holds Primelane's LAST signature until BOTH contract instruments are executed and is a server
 * refusal this board mirrors (`siblingSignatureOutstanding`, which answers for the advance too);
 * this one holds the FIRST move, asks the driver's arm alone, and is the client's presentation of
 * the owner's sequence rather than a refusal being mirrored. They are deliberately separate: this
 * lock LIFTS for an advance somebody has already signed, and the countersign gate must not — a
 * signature already given is not evidence about a witness who has not signed.
 */
export function advanceLockedFor(slot: BoardSlot): boolean {
    if (slot.docType !== 'INSURANCE_LOAN' && slot.docType !== 'LOAN') return false;
    if (slot.driverSigned) return false;
    if (slot.legacySignedAt) return false;
    return !(slot.document?.signatures ?? []).some(
        (signature) => signature.party !== 'COMPANY_REP' && signature.status === 'SIGNED',
    );
}

// ─── The hire purchase agreement's dependency on the vehicle's owner ─────────

/**
 * Why the hire purchase agreement cannot be started yet. The advance's sentence, one step earlier.
 *
 * IT NAMES THE PARTNER AND NOT THE DOCUMENT'S ABSENCE, because both causes of the server's refusal
 * end in the same act: the schedule has never been raised, or one is out and unsigned, and either
 * way what is waited on is a signature from the vehicle's owner. The row that takes it is directly
 * above this one on the same canvas.
 */
export const PARTNER_SCHEDULE_LOCK_REASON =
    'The vehicle’s partner has to sign the vehicle schedule first.';

/**
 * Whether the hire purchase agreement is still held shut by the vehicle's owner [D-B48].
 *
 * `advanceLockedFor`'S SHAPE, ONE LINK EARLIER IN THE SAME CHAIN. The server refuses an in-app
 * DRIVER signature on a hire purchase agreement while the contract's vehicle belongs to a
 * third-party partner who has not executed a Vehicle Schedule about that vehicle at the fee the
 * contract now charges — so a board that offered Generate would put a document in the driver's
 * inbox that `agreement_sign` answers with `AGREEMENT_PRIOR_SIGNATURE_REQUIRED`, on a page the
 * driver cannot see the cause on: `can_read_agreement_document` takes the partner's schedule away
 * from them entirely.
 *
 * ITS THREE LIMBS ARE THE SERVER'S THREE, DERIVED IN `buildBoardSlots` AND READ HERE. The referent
 * must be about the contract's current vehicle, at the fee the contract currently charges, and
 * carry either its own seal or a SIGNED PARTNER seat on the document arced to IT — not on any
 * `PARTNER_MANAGEMENT` document the contract happens to hold. `partnerScheduleSigned` is that whole
 * conjunction, which is why this predicate reads one flag and not three.
 *
 * IT HOLDS THE HIRE PURCHASE AGREEMENT AND NOTHING ELSE. The witness agreement waits on the
 * driver's signature, which this gate holds transitively; the premium advance waits on the same
 * one; the deposit agreement and the schedule itself are not downstream of it at all. One rule,
 * one row.
 *
 * IT IS A GATE ON STARTING — `advanceLockedFor`'s ruling, and its three lifts, verbatim. Once the
 * hire purchase agreement carries a signature of its own (a SIGNED counterparty seat, or a paper
 * original on the legacy row), the lock comes off whatever the schedule is doing: re-locking a
 * document a party has already signed would hide their signature behind a padlock, and that shape
 * is reachable — a schedule can be voided under a hire purchase agreement already in flight. What
 * the server does then is refuse the NEXT signature and say why, which is the right end of the
 * ceremony for it.
 *
 * THE HOUSE FLEET NEVER REACHES IT. `partnerScheduleRequired` is false on 259 of the 289 vehicles,
 * and on those contracts this is not a lock that lifts — it is a rule that does not apply.
 */
export function partnerScheduleLockedFor(slot: BoardSlot): boolean {
    if (slot.docType !== 'HIRE_PURCHASE') return false;
    if (!slot.partnerScheduleRequired) return false;
    if (slot.partnerScheduleSigned) return false;
    if (slot.driverSigned) return false;
    if (slot.legacySignedAt) return false;
    return !(slot.document?.signatures ?? []).some(
        (signature) => signature.party !== 'COMPANY_REP' && signature.status === 'SIGNED',
    );
}

/**
 * Why a void-and-reissue of this slot would withdraw a document it cannot replace, or null [D-B59].
 *
 * THE ROW'S CASH-PRICE BLOCK LIFTS UNDER A LIVE SCHEDULE, AND THE REISSUE'S QUESTION DOES NOT.
 * `buildBoardSlots` stops asking once a document exists, because that document has already copied
 * its figure. The void releases the copy: `agreement_create` then re-copies `contracts.cash_price`
 * and refuses a NULL or nothing with `AGREEMENT_CASH_PRICE_REQUIRED`. Past DRAFT a cleared cash
 * price does not withdraw the schedule, so a live one can sit over a contract that states none —
 * and voiding it then succeeds while its reissue does not, leaving the contract with no schedule.
 *
 * ASKED BEFORE ANYTHING IS WITHDRAWN, BY BOTH HALVES OF THE GESTURE. The void dialog holds its
 * confirm shut on this sentence while the reissue box is ticked, and the void mutation refuses on
 * it before the first void. It is `partnerScheduleCashPriceRefusal`, so the board, the generate
 * dialog and the void dialog say one sentence. Every other doc type answers null here.
 */
export function reissueRefusalFor(slot: BoardSlot): string | null {
    return slot.docType === 'PARTNER_MANAGEMENT'
        ? partnerScheduleCashPriceRefusal(slot.cashPrice)
        : null;
}

// ─── Which part of setup a document belongs to ───────────────────────────────

/** The setup areas a signable document can belong to. */
export type SlotStep = 'deposit' | 'insurance' | 'agreements';

/**
 * Where a document is managed, by its type.
 *
 * TOTAL OVER `AgreementDocType`, deliberately: a partial map returns undefined for a type
 * somebody adds later and the row silently disappears from every partition. The two named
 * cases are the ones with a home of their own — the deposit is papered where the money is
 * recorded, and the insurance loan where the loan is — and everything the contract itself is
 * a party to (hire purchase, witness, plain loans) is signed on the agreements area.
 *
 * THE VEHICLE SCHEDULE TAKES THE DEFAULT, AND THAT IS A DECISION [D-B48]. It has no home of its
 * own on this canvas: its figures are the contract's, it is executed in the same ceremony as the
 * hire purchase agreement and it is the row directly above the instrument it gates. Filing it
 * under the vehicle or the financials would put the two ends of one sequence on two screens. No
 * code changes for it — the default answers — and the sentence is here so the omission reads as
 * an answer rather than an oversight.
 */
export function stepForDocType(docType: AgreementDocType): SlotStep {
    if (docType === 'DEPOSIT') return 'deposit';
    if (docType === 'INSURANCE_LOAN') return 'insurance';
    return 'agreements';
}

/**
 * The slots split by the area that shows them.
 *
 * EVERY SLOT LANDS SOMEWHERE, AND NOWHERE TWICE. `stepForDocType` is total, so the three
 * buckets partition the input: nothing a contract can sign can go missing from the page
 * because no area claimed it.
 */
export function partitionSlotsByStep(slots: BoardSlot[]): Record<SlotStep, BoardSlot[]> {
    const byStep: Record<SlotStep, BoardSlot[]> = { deposit: [], insurance: [], agreements: [] };
    for (const slot of slots) byStep[stepForDocType(slot.docType)].push(slot);
    return byStep;
}

// ─── One policy year's share of the board [D-B45] ────────────────────────────

/**
 * The insurance slots for ONE loan — the renewal door's partition [D-B45].
 *
 * THE SETUP CANVAS PARTITIONS BY AREA; THE INSURANCE TAB PARTITIONS BY POLICY YEAR, and that is a
 * different question rather than a narrower one. `partitionSlotsByStep` hands the whole
 * `insurance` bucket to one list because the setup canvas is about ONE junction, so the bucket and
 * the year are the same set. On Contract Insights the board is read at `junctionScope: 'all'` and
 * the bucket holds every year at once — so a card that drew the bucket would offer year one's
 * ceremony inside year three's policy, and the send confirmation would name a document about a
 * premium that is not the one on screen.
 *
 * IT MATCHES ON THE SUBJECT ID, WHICH IS WHAT `agreement_create` IS CALLED WITH. The slot's subject
 * is the `insurance_loans` row; a policy's loan is that same row as `get_contract_insights` reports
 * it. Nothing else on either side is a shared identifier — the POLICY id and the JUNCTION id are
 * both a join away and neither appears on a slot.
 *
 * NO LOAN, NO SLOTS. A policy year with nothing recorded against it has no subject at all, so
 * there is nothing to generate: the caller says so in words rather than drawing an empty row that
 * looks like an unsent document.
 *
 * AND THE `#N` COMES OFF. `label` is `multiple()`'s ordinal — a qualifier that exists to tell
 * several rows of one type APART inside one list, so it means nothing in a list of one. Worse than
 * nothing here: it counts junctions in id order while the policy cards are drawn newest cover
 * first, so the top card would read `#2` and the one under it `#1`. The instrument's own title is
 * the whole name a per-policy row needs.
 */
export function insuranceSlotsForLoan(
    slots: BoardSlot[],
    loanId: number | null | undefined,
): BoardSlot[] {
    if (loanId === null || loanId === undefined) return [];
    return slots
        .filter((slot) => slot.docType === 'INSURANCE_LOAN' && slot.subjectId === loanId)
        .map((slot) => (slot.label === undefined ? slot : { ...slot, label: undefined }));
}

/**
 * The slots for ONE standing advance — `insuranceSlotsForLoan`'s twin, on the other subject table.
 *
 * THE SAME PARTITION AND THE SAME REASON. The board Contract Insights reads holds every advance the
 * contract has ever opened, and a card that drew the whole list would offer advance #1's ceremony
 * inside advance #3's card — where the send confirmation names the document it is about to mint, so
 * the admin would be agreeing to the wrong sentence on the click that calls `agreement_create`.
 *
 * THE DOC TYPE IS HALF THE MATCH, NOT A DECORATION ON IT. `loans` ids and `insurance_loans` ids are
 * two sequences in one integer space, and so are `contract_deposits` ids: a filter on the subject
 * alone hands an advance's card the insurance year's row whenever the two numbers coincide.
 *
 * AND THE `#N` COMES OFF, for the reason it comes off there: `label` exists to tell several rows of
 * one type apart inside one list, and it says nothing in a list of one.
 */
export function loanSlotsForLoan(
    slots: BoardSlot[],
    loanId: number | null | undefined,
): BoardSlot[] {
    if (loanId === null || loanId === undefined) return [];
    return slots
        .filter((slot) => slot.docType === 'LOAN' && slot.subjectId === loanId)
        .map((slot) => (slot.label === undefined ? slot : { ...slot, label: undefined }));
}

/** 'dd MMM yyyy' in the business timezone — the compact shape the advance card's grid prints. */
const compactBusinessDate = (value: string | null | undefined): string => {
    const day = businessDay(value);
    return day ? format(day, 'dd MMM yyyy') : '-';
};

/**
 * WHERE ONE ADVANCE'S SCHEDULE HAS GOT TO, in a chip — read off the slot, never off a date column.
 *
 * THE FIELD USED TO BE `loan_agreements.signed_at` OR THE WORD "Not signed", and between those two
 * answers sits the whole ceremony: a Schedule out with the borrower, one waiting on Primelane's
 * countersignature, one whose executed PDF is still being filed. All of them printed "Not signed" —
 * the same sentence as an advance nobody had papered at all.
 *
 * IT LIVES HERE RATHER THAN ON THE CARD BECAUSE IT IS A VOCABULARY, NOT A LAYOUT. vitest runs
 * `src/**` + `*.test.ts` under node, so a predicate declared inside a `.tsx` is reachable by no
 * test at all — and this is the one piece of the card that puts words to a signed instrument's
 * state. Beside `loanSlotsForLoan` and `sealStateFor` it is pinned like every other derivation on
 * this layer.
 *
 * THERE IS NO "DRAFT", AND THAT IS THE BOARD'S OWN MODEL [F0]. A document exists or it does not,
 * and once it does the driver can already see it: `get_my_pending_signatures` covers
 * PENDING_REVIEW, `agreement_sign` accepts it, and a LOAN document leaves that status only when
 * the borrower signs — `agreement_issue_token` refuses every party but WITNESS and DRIVER, so
 * there is no send step in between to be a draft. Calling it one told an admin an advance was
 * unsent for the whole window in which the driver was being asked to sign it, while the signing
 * row beside it said "Waiting on the Borrower." about the same document. So the two live statuses
 * collapse onto the SEAT: the borrower has signed, or they have not.
 *
 * AND A SEAL THAT FAILED IS NOT "Sealing". The finalize call fires once per document per session
 * and never retries, so after it fails nothing is in flight and nothing will be — but this card
 * holds no `SealAttempt` and cannot tell the three apart (`sealStateFor` is where that split lives,
 * on the board, which is where the attempt is known). It says the neutral truth instead: every
 * party has signed and the PDF is not stored yet, without claiming a request is out.
 *
 * THE PAPER ANSWER IS THE ABSENCE OF A DOCUMENT, WHICH IS `AgreementDocumentRow`'S OWN TEST. All
 * 161 production advances are paper: a signed `loan_agreements` row and no in-app document, which
 * `agreement_create` refuses to add one to (`AGREEMENT_ALREADY_SCANNED`). An in-app advance sets
 * the same `signed_at` at the seal, so the live document is what tells the two apart.
 *
 * NO SLOT IS NOT A STATE OF THE ADVANCE. The board is three round trips behind the tiles, and a
 * loan the subjects read has not returned yet would otherwise be announced as ungenerated on every
 * first paint. The dash says "not read", the way every other absent value on that card does.
 */
export function advanceAgreementStatus(slot: BoardSlot | null | undefined): string {
    if (!slot) return '-';

    const document = slot.document;
    if (!document) {
        return slot.legacySignedAt
            ? `Signed on paper ${compactBusinessDate(slot.legacySignedAt)}`
            : 'Not generated';
    }

    if (document.status === 'COMPLETED') {
        if (!document.finalized_at) return 'Executed — PDF being filed';
        const executed = slot.legacySignedAt ?? document.completed_at;
        return executed ? `Executed ${compactBusinessDate(executed)}` : 'Executed';
    }

    // PENDING_REVIEW and IN_SIGNING alike. Primelane countersigns last, so the only seat that can
    // be open beside the borrower's is its own — and a signed counterparty says which of the two.
    const borrowerSigned = document.signatures.some(
        (signature) => signature.party !== 'COMPANY_REP' && signature.status === 'SIGNED',
    );
    return borrowerSigned ? 'Awaiting countersignature' : 'Awaiting borrower';
}

/**
 * What the per-policy panel says when it has no row to draw — THE FOUR WAYS THAT HAPPENS.
 *
 * A SLOT IS A DOCUMENT THAT COULD EXIST, so "no rows" never means "nothing has been generated": an
 * ungenerated instrument IS a row, carrying the Send button. The list is empty only where there is
 * no SUBJECT — and the ways to have no subject are four different sentences, three of which are not
 * about this policy at all.
 *
 * IN FLIGHT IS NOT EMPTY, and it is asked first. THREE round trips stand behind this panel — the
 * contract row, the subjects and the documents — and `buildBoardSlots` returns nothing until the
 * first two have landed, so a card expanded early would otherwise announce that a premium nobody
 * can see yet does not exist. (`loading` covers all three; it used to omit the contract row.)
 *
 * REFUSED IS NOT EMPTY EITHER, AND IT OUTRANKS THE POLICY'S OWN STATE. If one of those reads came
 * back an error the panel has no model, and every sentence below it would be a claim about a board
 * nobody read. An unread board is reported as unread.
 *
 * A POLICY WITH NO LOAN IS THE ORDINARY CASE, not a fault: **54** junctions carry no insurance loan
 * at all — the same count measured on production and on staging (2026-08-31). It names the two
 * shapes the policy dossier can record — the advance and the payment — because which one applies is
 * the admin's answer, not this panel's [D-B45].
 *
 * THE LAST CASE IS A DISAGREEMENT BETWEEN TWO READS THAT BOTH SUCCEEDED, and it says so rather than
 * inventing a cause. `get_contract_insights` reported the loan and the board's subjects read did
 * not, which after a save that has not yet been invalidated resolves itself on the next read.
 */
export function insurancePanelEmptyText(state: {
    hasLoan: boolean;
    loading: boolean;
    failed: boolean;
}): string {
    if (state.loading) return 'Loading this contract’s signing model…';
    if (state.failed) {
        return 'This contract’s signing model could not be read, so nothing can be shown here. ' +
            'Reload the page.';
    }
    if (!state.hasLoan) {
        return 'Nothing is recorded against this policy year yet. Add the premium advance, or the ' +
            'payment the driver made, and the document to sign appears here.';
    }
    return 'This year’s premium is not on the signing board yet. Reload the page if you have just recorded it.';
}

// ─── Sealing the executed PDF ────────────────────────────────────────────────

/**
 * What THIS SESSION knows about the finalize call for one document.
 *
 * The server records only the outcome — `finalized_at` is set or it is not — so a request
 * that is running, and one that came back with a reason, exist nowhere but here.
 */
export interface SealAttempt {
    status: 'running' | 'failed';
    /** The reason the server gave, verbatim. Only on a failure. */
    message?: string;
    /** The structured code behind it, e.g. `FONTS_MISSING`. */
    code?: string | null;
}

export type SealPhase = 'none' | 'sealed' | 'sealing' | 'failed' | 'ready';

export interface SealState {
    phase: SealPhase;
    /** Shown verbatim on `failed`, null everywhere else. */
    message: string | null;
    code: string | null;
}

/** A failure the transport lost the sentence for still has to say something. */
export const SEAL_FAILED_FALLBACK =
    'The executed PDF could not be generated, and the server gave no reason.';

const NO_SEAL: SealState = { phase: 'none', message: null, code: null };

/**
 * Which of the four seal states a row is in.
 *
 * "EVERY PARTY HAS SIGNED AND THE PDF IS NOT STORED" IS NOT ONE STATE, IT IS THREE, and
 * showing a spinner for all of them is what left the board finalizing forever: the request
 * fires once per document per session, so after it fails — brand fonts not uploaded, a cold
 * isolate timing out — nothing is in flight, nothing will retry, and the row was animating
 * over a request nobody was making. The reason lived in a toast that was gone in seconds.
 *
 * So the phase is read off the attempt, not off the document alone: `sealing` only while a
 * call is genuinely out, `failed` for as long as the reason is unaddressed, and `ready` for
 * the resolvable case where the admin has to ask for it. `finalized_at` outranks everything
 * — a stored PDF is the truth whatever this tab believes it is still doing.
 */
export function sealStateFor(
    document: AgreementDocumentRow | null | undefined,
    attempt: SealAttempt | null | undefined,
): SealState {
    if (!document || document.status !== 'COMPLETED') return NO_SEAL;
    if (document.finalized_at) return { phase: 'sealed', message: null, code: null };
    if (attempt?.status === 'running') return { phase: 'sealing', message: null, code: null };
    if (attempt?.status === 'failed') {
        return {
            phase: 'failed',
            message: attempt.message?.trim() || SEAL_FAILED_FALLBACK,
            code: attempt.code ?? null,
        };
    }
    return { phase: 'ready', message: null, code: null };
}

/**
 * Whether the board has anything left to learn from a refetch.
 *
 * Two situations move without this tab doing anything: a party signing somewhere else, and
 * a finalization that has been claimed but not finished. Everything else — a document
 * nobody has generated, one whose parties have all signed and whose PDF is stored — is
 * static until an admin acts, and polling it is pure noise.
 */
export function documentsAreOut(documents: AgreementDocumentRow[]): boolean {
    return documents.some((doc) => {
        if (doc.status === 'COMPLETED') return !doc.finalized_at;
        if (doc.status !== 'IN_SIGNING' && doc.status !== 'PENDING_REVIEW') return false;
        return doc.signatures.some(
            (signature) =>
                signature.status === 'PENDING' ||
                signature.status === 'SENT' ||
                signature.status === 'VIEWED',
        );
    });
}
